// =============================================================
// SITE CONTENT — edit this single file to update the portfolio.
// =============================================================

export const PROFILE = {
  name: "Yuvakumar",
  role: "UX Director",
  fullTitle: "Associate UX Director — APAC",
  location: "Chennai, India",
  email: "yuva.kumar.design@gmail.com",
  phone: "+91 98415 70819",
  linkedin: "https://linkedin.com/in/yuvap",
};

export const HERO = {
  hook: "Bridging human behavior with business metrics to scale digital products.",
  story: [
    { lead: "Pixel-obsessed designer", rest: " turned builder of design organizations that ship to millions." },
    { lead: "Scaled 6 → 60+", rest: " — with the audit frameworks that win enterprise pursuits and Agentic AI woven into the craft." },
    { lead: "Where intuition meets the P&L", rest: " — ambiguous problems in, award-winning products out." },
  ],
  videos: [
    "https://assets.mixkit.co/videos/44818/44818-1080.mp4",
    "https://videos.pexels.com/video-files/6561926/6561926-hd_1920_1080_25fps.mp4",
  ],
  ctas: [
    { label: "View Case Studies", to: "/case-studies", primary: true },
  ],
};

export const SKILLS = [
  "UX Strategy",
  "AI-Built-In Design",
  "Design System Governance",
  "Workshop Facilitation",
  "Design Ops",
  "Design Delivery",
  "Team Building",
  "Human-AI Interaction",
  "Enterprise Scale",
  "Research & Discovery",
];

export const METRICS = [
  { value: "20+", label: "Years scaling UX", sub: "APAC enterprise & consulting" },
  { value: "6 → 60+", label: "Practice scaled", sub: "Hiring & career frameworks built" },
  { value: "2.4M+", label: "Users reached", sub: "Products shipped globally" },
  { value: "40+", label: "Client pursuits / yr", sub: "Powered by my UX Audit framework" },
];

export const MANIFESTO = [
  {
    n: "01",
    title: "Behavior is the brief",
    body: "Every metric that matters — retention, conversion, trust — is a fossil of human behavior. I design for the behavior first; the numbers follow.",
  },
  {
    n: "02",
    title: "Leadership is a system",
    body: "Great design at scale is not heroics. It is hiring, onboarding, career ladders, and audit frameworks that let 60 people ship like 6.",
  },
  {
    n: "03",
    title: "AI is a co-designer",
    body: "I founded an AI Guild to weave Generative & Agentic AI into the UX lifecycle — augmenting research, ideation, and delivery across 25+ teams.",
  },
];

export const TESTIMONIAL = {
  quote:
    "Yuva leads the way great directors should — he builds the system, then gets out of the way. Lowest attrition in the org, highest craft on the floor, every single year.",
  author: "Practice Leadership",
  role: "BORN, Tech Mahindra",
};

export const CLIENTELE_INTRO = {
  title: "How I Scale Enterprise Partner Relationships",
  body: [
    "Enterprise partnerships are not won in the pitch room — they are earned in the maturity conversation. My UX Audit framework gives sales and delivery a shared language to assess a client's current state and co-author a roadmap they believe in.",
    "From luxury (Cartier) to telecom (Verizon, Telefónica, Digi) to CPG (PepsiCo, Oreo, Reebok), I translate brand ambition into measurable experience strategy — then staff, structure, and mentor the teams that deliver it across APAC.",
  ],
};

export const LOGO_TOKEN = "pk_X-1ZO13GSgeOoUrIuJ6GMQ";

// Ordered so the first row (4 cols) does NOT contain Cartier — Cartier sits on the 2nd row.
export const BRANDS = [
  { name: "PepsiCo", sector: "CPG", domain: "pepsico.com" },
  { name: "Verizon", sector: "Telecom", domain: "verizon.com" },
  { name: "Intel", sector: "Technology", domain: "intel.com" },
  { name: "Nokia", sector: "Technology", domain: "nokia.com" },
  { name: "Cartier", sector: "Luxury", domain: "cartier.com" },
  { name: "Reebok", sector: "Sportswear", domain: "reebok.com" },
  { name: "Telefónica", sector: "Telecom", domain: "telefonica.com" },
  { name: "SBI", sector: "Banking", domain: "sbi.co.in" },
  { name: "Oreo", sector: "CPG", domain: "oreo.com" },
  { name: "Mahindra Rise", sector: "Conglomerate", domain: "mahindra.com" },
  { name: "Aditya Birla Group", sector: "Conglomerate", domain: "adityabirla.com" },
  { name: "Godrej Interio", sector: "Retail", domain: "godrejinterio.com" },
  { name: "Mobily", sector: "Telecom", domain: "mobily.com.sa" },
  { name: "Lubrizol", sector: "Chemicals", domain: "lubrizol.com" },
  { name: "TPG Telecom", sector: "Telecom", domain: "tpgtelecom.com.au" },
  { name: "iShop Changi", sector: "Retail / Travel", domain: "ishopchangi.com" },
  { name: "Digi", sector: "Telecom", domain: "digi.com.my" },
  { name: "Fairview", sector: "Education", domain: "fairview.edu.my" },
  { name: "Archaeological Survey of India", sector: "Government / Culture", domain: "asi.nic.in" },
];

export const SECTORS = [
  "Luxury & Fashion",
  "Telecommunications",
  "Consumer Packaged Goods",
  "Enterprise SaaS",
  "Retail & Travel",
  "Healthcare",
  "Education (K-12)",
  "Fintech & Remittance",
];

export const CASE_STUDIES = [
  {
    slug: "tpg-telecom-design-system",
    title: "TPG Telecom — Multi-Brand Design System",
    client: "TPG Telecom",
    year: "2023 — 2024",
    tag: "Design Systems",
    summary:
      "A ground-up design-system revamp — from discovery and audit to a multi-brand, white-label architecture that powers a portfolio of telecom brands from a single source of truth.",
    image: "https://images.unsplash.com/photo-1487958449943-2429e8be8625?q=80&w=1600&auto=format&fit=crop",
    video: "https://videos.pexels.com/video-files/3253298/3253298-hd_1920_1080_25fps.mp4",
    problem:
      "TPG Telecom ran multiple consumer brands on fragmented, duplicated UI — inconsistent experiences, slow delivery, and ballooning design and engineering cost with every new brand or campaign.",
    role:
      "I led the design-system revamp end-to-end — running discovery and a full UX/UI audit, defining the multi-brand token architecture and governance model, and aligning design, engineering and brand stakeholders.",
    strategy: [
      "Ran discovery interviews and a component-level audit to map redundancy and inconsistency across brands.",
      "Architected a white-label token system — a brand-agnostic core with per-brand theme layers — so one component set skins to any brand.",
      "Built accessible, documented components with contribution workflows and governance for federated teams.",
      "Piloted on a flagship brand, then rolled out as the single source of truth across the portfolio.",
    ],
    outcome: [
      { value: "Multi-brand", label: "One system, every brand" },
      { value: "Faster", label: "Time-to-market for new brands" },
      { value: "Consistent", label: "Accessible experience at scale" },
    ],
  },
  {
    slug: "pepsico-kiosk-shift-left",
    title: "PepsiCo — Self-Serve Kiosk Experience",
    client: "PepsiCo",
    year: "2022 — 2023",
    tag: "Shift-Left UX",
    summary:
      "A self-service kiosk solution designed with a shift-left UX approach — embedding research and usability upstream so quality was engineered in, not inspected in.",
    image: "https://images.unsplash.com/photo-1613652038578-a9a988b54a60?q=80&w=1600&auto=format&fit=crop",
    problem:
      "Kiosk programs traditionally surfaced usability and accessibility issues late — in QA or after launch — driving costly rework, abandoned transactions, and frustrated shoppers.",
    role:
      "As UX lead, I introduced a shift-left approach — moving research, prototyping and usability validation to the earliest stages and pairing designers with engineering and hardware teams from day one.",
    strategy: [
      "Embedded rapid research and testable prototypes into discovery, validating flows before a line of code.",
      "Co-created acceptance criteria with engineering so UX and accessibility were part of the definition of done.",
      "Designed for real-world constraints early — glare, gloves, queues, and accessibility reach ranges.",
      "Instrumented the kiosk to close the loop with post-launch behavioral data.",
    ],
    outcome: [
      { value: "Shift-Left", label: "Defects caught upstream" },
      { value: "Fewer", label: "Late-stage reworks" },
      { value: "Higher", label: "Task completion at the kiosk" },
    ],
  },
  {
    slug: "sbi-yono-business-governance",
    title: "SBI YONO Business — Design Governance at Scale",
    client: "State Bank of India",
    year: "2023 — Present",
    tag: "Design Ops & Governance",
    summary:
      "A design-governance layer for YONO Business — the operating model that let a large, distributed design team deliver consistent, compliant value to millions of business customers.",
    image: "https://images.unsplash.com/photo-1602576666092-bf6447a729fc?q=80&w=1600&auto=format&fit=crop",
    problem:
      "Scaling a large design team on a mission-critical banking platform risked inconsistency, compliance gaps, and slower delivery without a shared operating model and quality bar.",
    role:
      "I created and led the design-governance layer — defining standards, review rituals, and a scalable operating model that let a big team move fast without breaking consistency or compliance.",
    strategy: [
      "Established a governance framework: design principles, review gates, and a shared quality bar.",
      "Built a component and pattern library with accessibility and banking-compliance baked in.",
      "Set up design-ops rituals and career ladders so a large team scaled without diluting craft.",
      "Instituted metrics to track consistency, velocity, and customer-value delivery.",
    ],
    outcome: [
      { value: "Large team", label: "Scaled with consistency" },
      { value: "Compliant", label: "By design, not by audit" },
      { value: "Customer value", label: "Delivered at scale" },
    ],
  },
  {
    slug: "mahindra-automotive-touchpoints",
    title: "Mahindra Automotive — Digital Touchpoints for a House of Brands",
    client: "Mahindra Automotive",
    year: "2021 — 2022",
    tag: "Experience Design",
    summary:
      "Award-winning digital experiences unifying the customer journey across Mahindra's house of automotive brands — from discovery to ownership.",
    image: "https://images.unsplash.com/photo-1517026575980-3e1e2dedeab4?q=80&w=1600&auto=format&fit=crop",
    problem:
      "A house of automotive brands meant fragmented digital touchpoints and inconsistent customer journeys from discovery through purchase and ownership.",
    role:
      "I led experience design across the house of brands — crafting a cohesive yet brand-expressive system of digital touchpoints and aligning stakeholders across the portfolio.",
    strategy: [
      "Mapped the end-to-end automotive journey across discovery, configuration, purchase, and ownership.",
      "Designed a flexible experience framework that expressed each brand while staying cohesive.",
      "Elevated craft with immersive product visualization and configurators.",
      "Validated with users and iterated toward measurable engagement gains.",
    ],
    outcome: [
      { value: "Award-winning", label: "Recognized digital work" },
      { value: "Unified", label: "Journey across brands" },
      { value: "Higher", label: "Engagement & lead quality" },
    ],
  },
  {
    slug: "mahindra-cxo-dashboard",
    title: "Mahindra — CXO Decision Dashboard",
    client: "Mahindra Group",
    year: "2022 — 2023",
    tag: "Data Experience",
    summary:
      "An executive decision dashboard that turned scattered group-wide data into a single, glanceable command center for CXOs — clarity over clutter, decisions over dashboards.",
    image: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?q=80&w=1600&auto=format&fit=crop",
    problem:
      "CXOs were drowning in disconnected reports and vanity charts — no single, trustworthy view to steer the business, and every team told a different story with the same data.",
    role:
      "I led the experience strategy and design — interviewing executives to model real decisions, then shaping an information hierarchy and visual system that put signal before noise.",
    strategy: [
      "Ran executive interviews to map the actual decisions each CXO makes and the questions behind them.",
      "Designed a decision-first information architecture — headline metrics, drill-downs on demand, alerts for exceptions.",
      "Built a restrained data-viz system (one accent, clear typographic hierarchy) to kill chart-junk.",
      "Validated with executives and iterated toward at-a-glance comprehension in seconds.",
    ],
    outcome: [
      { value: "Single view", label: "One source of executive truth" },
      { value: "Seconds", label: "To key insight, not minutes" },
      { value: "Confident", label: "Faster, data-backed decisions" },
    ],
  },
  {
    slug: "ux-audit-framework",
    title: "The UX Audit Framework",
    client: "BORN, Tech Mahindra",
    year: "2021 — Present",
    tag: "Practice Strategy",
    summary:
      "A repeatable diagnostic that assesses a client's current-state maturity and hands sales a defensible roadmap — now standard in 40+ pursuits a year.",
    image: "https://images.unsplash.com/photo-1522542550221-31fd19575a2d?q=80&w=1600&auto=format&fit=crop",
    problem:
      "Enterprise sales teams were entering pursuits without an objective way to assess a prospect's UX maturity, leading to inconsistent scoping, misaligned expectations, and lost deals.",
    role:
      "As Associate UX Director, I owned the strategy end-to-end — designing the framework, training sales and delivery, and embedding it into the pursuit process across APAC.",
    strategy: [
      "Codified UX maturity into measurable dimensions across research, systems, ops, and leadership.",
      "Built a scoring rubric and a visual current-state → future-state roadmap artifact.",
      "Ran enablement workshops so non-designers could run credible audits in the sales cycle.",
    ],
    outcome: [
      { value: "40+", label: "Client pursuits / year" },
      { value: "Standardized", label: "Scoping across APAC" },
      { value: "Higher", label: "Pitch-to-win conversion" },
    ],
  },
  {
    slug: "ai-native-ux",
    title: "AI Guild — Agentic UX Lifecycle",
    client: "BORN, Tech Mahindra",
    year: "2022 — Present",
    tag: "AI-Native Product Design",
    summary:
      "Founded and led an AI Guild embedding Generative & Agentic AI into research, ideation and delivery — adopted by 25+ project teams.",
    image: "https://images.unsplash.com/photo-1558655146-d09347e92766?q=80&w=1600&auto=format&fit=crop",
    problem:
      "Design teams risked being left behind by the AI shift — using it ad-hoc, unsafely, and without a shared craft standard for human-AI interaction.",
    role:
      "Founder & lead of the AI Guild. Set the vision, curated the toolchain, and mentored designers on Human-AI interaction patterns.",
    strategy: [
      "Mapped where Generative & Agentic AI creates leverage across the UX lifecycle.",
      "Established reusable prompts, patterns, and guardrails for human-AI interaction design.",
      "Ran guild sessions to upskill designers and diffuse practice across teams.",
    ],
    outcome: [
      { value: "25+", label: "Teams adopted" },
      { value: "Faster", label: "Research → concept" },
      { value: "New", label: "AI-native offerings" },
    ],
  },
  {
    slug: "nlp-web-builder",
    title: "NLP-Driven Web Builder",
    client: "Brane Enterprises",
    year: "2020 — 2021",
    tag: "Enterprise Product",
    summary:
      "Designed the end-to-end flow for an NLP web builder and an AI visualization tool that cut dashboard creation from days to under 15 minutes.",
    image: "https://images.unsplash.com/photo-1541462608143-67571c6738dd?q=80&w=1600&auto=format&fit=crop",
    problem:
      "Building enterprise apps and dashboards was slow and specialist-bound, throttling go-to-market for AI/NLP product offerings.",
    role:
      "UX Solution Leader directing experience strategy for AI/NLP-driven enterprise applications.",
    strategy: [
      "Designed the end-to-end user flow for an NLP-based web builder shipped as a core product.",
      "Built a cross-functional UX delivery process to compress go-to-market time.",
      "Reimagined dashboard creation as an AI-led, conversational visualization tool.",
    ],
    outcome: [
      { value: "30%", label: "Faster go-to-market" },
      { value: "<15 min", label: "Dashboard creation (from 3–5 days)" },
      { value: "Core", label: "Product offering shipped" },
    ],
  },
];

export const EXPERIENCE = [
  {
    company: "BORN, Tech Mahindra",
    role: "Associate UX Director — APAC",
    period: "Oct 2021 — Present",
    achievements: [
      "Own UX strategy, research, creative sales and delivery across APAC.",
      "Scaled the UX practice from 6 to 60+ professionals; built hiring, onboarding & career frameworks.",
      "Built the UX Audit framework — standard in 40+ client pursuits a year.",
      "Facilitated design-thinking workshops across 50+ APAC engagements.",
      "Won Webby, w3, Hermes, COMM & DIGIES awards; lowest attrition of any function, every year.",
      "Founded the AI Guild — Generative & Agentic AI adopted by 25+ teams.",
    ],
    stack: ["UX Strategy", "Design Systems", "Agentic AI", "Design Ops", "Workshop Facilitation"],
  },
  {
    company: "Brane Enterprises",
    role: "UX Solution Leader",
    period: "Oct 2020 — Oct 2021",
    achievements: [
      "Directed experience strategy for AI/NLP-driven enterprise applications.",
      "Cut go-to-market time for enterprise apps by 30% with a new UX delivery process.",
      "Designed an NLP-based web builder shipped as a core product offering.",
      "Delivered an AI visualization tool reducing dashboard creation to under 15 minutes.",
    ],
    stack: ["NLP UX", "Generative AI", "Enterprise SaaS", "Service Design"],
  },
  {
    company: "Unimoni GBS",
    role: "UX Associate Director — India",
    period: "Jul 2019 — Aug 2020",
    achievements: [
      "Implemented a global component library with authoring capabilities.",
      "Standardized UX guidelines across internal and vendor development teams.",
      "Established a remote UX practice using gamification to sustain engagement.",
    ],
    stack: ["Design Systems", "Component Libraries", "Agile", "Remote Ops"],
  },
  {
    company: "Straive (SPi Global)",
    role: "UX Architect",
    period: "Feb 2017 — Jul 2019",
    achievements: [
      "Led UX for a K-12 supplementary education product across Android, iOS & desktop.",
      "Partnered with Product Owners, clients and engineers to align features to business goals.",
      "Designed team workshops and games to build alignment and motivation.",
    ],
    stack: ["Mobile UX", "EdTech", "Cross-platform", "Research"],
  },
  {
    company: "Quadrant4 Solutions",
    role: "UX Architect",
    period: "2015 — 2017",
    achievements: [
      "Built an authoring tool spanning the full creative lifecycle.",
      "Led UX for a healthcare SaaS application that won major industry awards.",
    ],
    stack: ["Healthcare SaaS", "Authoring Tools", "UX Design"],
  },
  {
    company: "Salzer Technologies",
    role: "UX Lead Engineer",
    period: "2013 — 2015",
    achievements: [
      "Managed a cross-functional design team.",
      "Delivered search UX for a business accreditation platform serving 2.4M+ users.",
    ],
    stack: ["Search UX", "Team Leadership", "Web Platforms"],
  },
  {
    company: "Fixity Technologies",
    role: "UX Lead",
    period: "2005 — 2012",
    achievements: [
      "Built a 15+ person creative team from the ground up.",
      "Partnered with Intel and Nokia to deliver marketplace applications.",
    ],
    stack: ["Team Building", "Marketplace UX", "Client Delivery"],
  },
];

export const EDUCATION = [
  { degree: "Bachelor of Computer Science", school: "DG Vaishnav College of Arts & Science", period: "1997 — 2000" },
  { degree: "Diploma in Multimedia", school: "Arena Multimedia", period: "2000 — 2001" },
];

export const NAV = [
  { label: "Clientele", to: "/clientele" },
  { label: "Case Studies", to: "/case-studies" },
  { label: "Experience", to: "/experience" },
  { label: "Blog", to: "/blog" },
  { label: "Contact", to: "/contact" },
];

// ---------------- Central media (edit here) ----------------
const U = (id, w = 1600) => `https://images.unsplash.com/photo-${id}?q=80&w=${w}&auto=format&fit=crop`;

export const MEDIA = {
  heroVideoDark: "https://assets.mixkit.co/videos/44818/44818-1080.mp4", // dark ink flowing
  heroVideoAlt: "https://assets.mixkit.co/videos/31497/31497-720.mp4", // 3D black cubes tunnel
  heroImageLight: U("1488972685288-c3fd157d7c7a", 2000), // bright modern architecture
  summaryImage: U("1587440871875-191322ee64b0", 1400), // ux design workspace
  ctaImage: U("1522542550221-31fd19575a2d", 1600), // team collaboration
};

export const CASE_STUDIES_INTRO =
  "From boardroom strategy to shipped pixels — a curated selection of engagements where I turned ambiguous, high-stakes problems into measurable business outcomes. Each study reads the way leadership does: the problem, my role, the methodology, and the numbers that moved.";
